import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useCoachStore } from '@store/coachStore';
import { useNavigate } from 'react-router-dom';
import AdminService, { Player } from '@services/adminService';
import PlayerSelfProgressSection from '@components/dashboard/PlayerSelfProgressSection';

const CoachDashboard: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { juniorCoaches } = useCoachStore();
  const navigate = useNavigate();

  const [myStudents, setMyStudents] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingJuniorCoaches, setLoadingJuniorCoaches] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadMyStudents();
      loadJuniorCoaches();
    }
  }, [user]);

  const resolveCoachId = async (): Promise<string | null> => {
    let coachId = (user as any)?.coachId;
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
          const updatedUser = {
            ...user,
            coachId: data.data.id,
            coachIdCode: data.data.coachIdCode,
            firstName: data.data.user.firstName,
            lastName: data.data.user.lastName,
          };
          useAuthStore.getState().setUser(updatedUser);
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }
    return coachId || null;
  };

  const loadMyStudents = async () => {
    const coachId = await resolveCoachId();
    if (!coachId) {
      setError('Coach ID not found. Please contact support if this issue persists.');
      return;
    }

    setLoading(true);
    try {
      const response = await AdminService.getPlayersForCoach(coachId);
      setMyStudents(response.data || []);
    } catch {
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadJuniorCoaches = async () => {
    const coachId = await resolveCoachId();
    if (!coachId) {
      useCoachStore.getState().setJuniorCoaches([]);
      return;
    }

    setLoadingJuniorCoaches(true);
    try {
      const response = await fetch(`/api/coaches/hierarchy/${coachId}`);
      const data = await response.json();

      if (data.success && data.data?.juniorCoaches) {
        const mapped = data.data.juniorCoaches.map((coach: any) => ({
          id: coach.id,
          coach_id_code: coach.coachIdCode || coach.coach_id_code,
          first_name: coach.firstName || coach.first_name,
          last_name: coach.lastName || coach.last_name,
          email: coach.user?.email || coach.email,
          assigned_sports: coach.assignedSports || coach.assigned_sports || [],
          coach_level: coach.coachLevel || coach.coach_level,
          academy_id: coach.academyId || coach.academy_id,
          is_active: coach.user?.isActive !== false,
          created_at: coach.createdAt || coach.created_at,
          updated_at: coach.updatedAt || coach.updated_at,
        }));
        useCoachStore.getState().setJuniorCoaches(mapped);
      } else {
        useCoachStore.getState().setJuniorCoaches([]);
      }
    } catch (err: any) {
      console.error('Error loading junior coaches:', err);
      useCoachStore.getState().setJuniorCoaches([]);
    } finally {
      setLoadingJuniorCoaches(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Coach Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Welcome, {(user as any)?.firstName || user?.firstName || ''}{' '}
            {(user as any)?.lastName || user?.lastName || ''} ({(user as any)?.coachIdCode || 'N/A'})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => navigate('/coach/profile')}>
            Profile
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(loading || loadingJuniorCoaches) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                My Students
              </Typography>
              <Typography variant="h3">{myStudents.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Players
              </Typography>
              <Typography variant="h3">{myStudents.filter((s) => s.user.isActive).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Sports Covered
              </Typography>
              <Typography variant="h3">
                {new Set(myStudents.map((s) => s.primarySport).filter(Boolean)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => navigate('/coach/junior-coaches')}
          >
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Junior Coaches
              </Typography>
              <Typography variant="h3">{juniorCoaches.length}</Typography>
              <Button size="small" startIcon={<GroupIcon />} sx={{ mt: 1, px: 0 }} onClick={(e) => {
                e.stopPropagation();
                navigate('/coach/junior-coaches');
              }}>
                Manage
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PlayerSelfProgressSection portalMode="coach" title="My Learning Progress" />
    </Container>
  );
};

export default CoachDashboard;
